import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';

const tokenFileProblem = (file) => (
  !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || !file.size || file.size > 5 * 1024 * 1024
    ? 'Choose a PNG, JPEG, or WebP image no larger than 5 MB.' : '');

const readTokenFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Could not read token image.'));
  reader.readAsDataURL(file);
});

function TokenCard({ asset, endpoint, onSaved, onDeleted, onError }) {
  const saved = Math.round((asset.displayScale ?? 1) * 100);
  const [percent, setPercent] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [draftName, setDraftName] = useState(null);
  const [notice, setNotice] = useState('');
  const replaceInput = useRef(null);
  const pendingReplace = useRef(null);
  const skipNameSave = useRef(false);
  const assetUrl = `${endpoint}/${encodeURIComponent(asset.assetId)}`;
  const name = asset.displayName || asset.filename || 'custom token';
  const usedBy = asset.usedBy || [];
  useEffect(() => { setPercent(saved); }, [saved]);
  const value = Number(percent);
  const patch = async (changes, failure, done) => {
    setSaving(true); onError(''); setNotice('');
    try {
      const response = await apiFetch(assetUrl, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || failure);
      onSaved(data);
      if (done) setNotice(done);
    } catch (err) { onError(err.message || failure); }
    finally { setSaving(false); }
  };
  const save = () => patch({ displayScale: value / 100 }, 'Could not save token scale.');
  const finishRename = () => {
    const next = draftName?.trim();
    setDraftName(null);
    if (skipNameSave.current) { skipNameSave.current = false; return; }
    if (next && next !== name) patch({ name: next }, 'Could not rename token.', 'Name saved');
  };
  const replace = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || saving) return;
    const problem = tokenFileProblem(file);
    if (problem) { onError(problem); return; }
    setSaving(true); onError(''); setNotice('');
    try {
      const image = await readTokenFile(file);
      if (pendingReplace.current?.image !== image) pendingReplace.current = { image, requestId: crypto.randomUUID() };
      const response = await apiFetch(`${assetUrl}/image`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...pendingReplace.current, filename: file.name }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not replace token image.');
      pendingReplace.current = null;
      onSaved(data);
      setNotice('Image replaced. Scale and characters kept');
    } catch (err) { onError(err.message || 'Could not replace token image. Select the file again to retry.'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Permanently delete ${name}? This can't be undone.`)) return;
    setSaving(true); onError('');
    try {
      const response = await apiFetch(assetUrl, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not delete token.');
      onDeleted(asset.assetId);
    } catch (err) { onError(err.message || 'Could not delete token.'); setSaving(false); }
  };
  return <>
    {/* The dashed box is one map square at 100%, so art can be judged against the grid. */}
    <div style={{ position: 'relative', width: 120, height: 120, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 20, border: '1px dashed rgba(255, 255, 255, 0.5)' }} />
      <img src={asset.secureUrl} alt={name} style={{ position: 'absolute', inset: 20, width: 80, height: 80, objectFit: 'contain', transform: `scale(${value > 0 ? value / 100 : 1})` }} />
    </div>
    <figcaption className="w-100">
      {draftName === null
        ? <Button variant="link" size="sm" className="p-0 text-light text-decoration-none mw-100 text-truncate" onClick={() => setDraftName(name)} disabled={saving} aria-label={`Rename ${name}`} title="Rename">
          {name} <i className="fa-solid fa-pen ms-1 small" aria-hidden="true" />
        </Button>
        : <Form.Control autoFocus size="sm" className="text-center" maxLength={60} value={draftName} aria-label={`Name for ${name}`}
          onChange={(event) => setDraftName(event.target.value)} onBlur={finishRename}
          onKeyDown={(event) => {
            if (event.key === 'Escape') skipNameSave.current = true;
            if (event.key === 'Enter' || event.key === 'Escape') event.currentTarget.blur();
          }} />}
      <div className="text-muted text-truncate" style={{ fontSize: '0.7rem' }} title={asset.filename}>{asset.filename}</div>
    </figcaption>
    <div className="small text-muted text-truncate w-100" title={usedBy.join(', ') || undefined}>{usedBy.length ? `Used by ${usedBy.join(', ')}` : 'Not in use'}</div>
    <div className="d-flex align-items-center justify-content-center gap-1 mt-1">
      <Form.Control type="number" size="sm" min={50} max={250} step={5} value={percent} onChange={(event) => setPercent(event.target.value)} aria-label={`Map scale percent for ${name}`} style={{ width: 72 }} />
      <span className="small">%</span>
      <Button size="sm" variant="outline-light" onClick={save} disabled={saving || !value || value === saved} aria-label={`Save map scale for ${name}`}>Save</Button>
    </div>
    <Form.Control ref={replaceInput} type="file" accept="image/png,image/jpeg,image/webp" className="visually-hidden" onChange={replace} disabled={saving} aria-label={`Replacement image for ${name}`} />
    <Button size="sm" variant="outline-light" className="mt-2 w-100" onClick={() => replaceInput.current?.click()} disabled={saving} aria-label={`Replace image for ${name}`}>Replace image</Button>
    <Button size="sm" variant="outline-danger" className="mt-1 w-100" onClick={remove} disabled={saving || usedBy.length > 0} aria-label={`Delete ${name}`}
      title={usedBy.length ? 'Choose another token for this character first' : undefined}>Delete</Button>
    {notice && <p role="status" className="small text-success mt-1 mb-0">{notice}</p>}
  </>;
}

export default function CustomPlayerTokens({ campaignId }) {
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const input = useRef(null);
  const pending = useRef(null);
  const endpoint = `/campaigns/${encodeURIComponent(campaignId)}/custom-player-tokens`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setAssets([]); setError(''); pending.current = null;
    apiFetch(endpoint).then(async (response) => {
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not load custom tokens.');
      if (!cancelled) setAssets(data.assets || []);
    }).catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint]);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || busy) return;
    const problem = tokenFileProblem(file);
    if (problem) { setError(problem); return; }
    setBusy(true); setError('');
    try {
      const image = await readTokenFile(file);
      if (pending.current?.image !== image) pending.current = { image, requestId: crypto.randomUUID() };
      const response = await apiFetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...pending.current, filename: file.name }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Token upload failed.');
      setAssets((previous) => [...previous.filter((asset) => asset.assetId !== data.assetId), data]);
      pending.current = null;
    } catch (err) { setError(err.message || 'Token upload failed. Select the file again to retry.'); }
    finally { setBusy(false); }
  };

  return <section className="mt-4 text-start" aria-labelledby="custom-player-tokens-heading">
    <h3 id="custom-player-tokens-heading" className="h5 text-light">Custom Player Tokens</h3>
    <p className="text-muted small">Upload figurines for this campaign. Players can choose them from Custom Tokens alongside their usual options. Transparent 400 × 400 PNG recommended; maximum 5 MB. Map scale changes how large the art looks, not the squares the token occupies.</p>
    {error && <Alert variant="danger">{error}</Alert>}
    <Form.Control ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="visually-hidden" onChange={upload} disabled={busy || loading} aria-label="Upload custom player token" />
    <Button variant="outline-light" className="mb-3" onClick={() => input.current?.click()} disabled={busy || loading}>
      {busy && <Spinner size="sm" className="me-2" />} {busy ? 'Uploading…' : 'Upload Custom Token'}
    </Button>
    {loading ? <p role="status">Loading custom tokens…</p> : <div className="d-flex flex-wrap gap-3">
      {assets.length === 0 && <p className="text-muted small">No custom player tokens uploaded yet.</p>}
      {assets.map((asset) => <figure key={asset.assetId} className="mb-2 d-flex flex-column align-items-center text-center" style={{ width: 150 }}>
        <TokenCard asset={asset} endpoint={endpoint} onError={setError}
          onSaved={(saved) => setAssets((previous) => previous.map((entry) => entry.assetId === saved.assetId ? { ...entry, ...saved } : entry))}
          onDeleted={(assetId) => setAssets((previous) => previous.filter((entry) => entry.assetId !== assetId))} />
      </figure>)}
    </div>}
  </section>;
}
